import { useState, useEffect, type FormEvent } from "react";
import { format, parseISO, parse, isAfter, isBefore, addMinutes } from "date-fns";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Loader2, ChevronsUpDown, Check } from "lucide-react";
import { formatDuration } from "@/lib/time-utils";
import { getProject } from "@/lib/time-api";
import type { TimeEntry, Project, Task } from "@/types/time";

interface TimeEntryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entry?: TimeEntry | null;
  defaultDate?: string;
  projects: Project[];
  existingEntries?: TimeEntry[];
  onSubmit: (data: {
    description?: string | null;
    projectId?: string | null;
    taskId?: string | null;
    start: string;
    end: string;
    createdWith?: string;
  }) => Promise<void>;
}

export function TimeEntryDialog({
  open,
  onOpenChange,
  entry,
  defaultDate,
  projects,
  existingEntries = [],
  onSubmit,
}: TimeEntryDialogProps) {
  const isEdit = !!entry;
  const today = format(new Date(), "yyyy-MM-dd");

  const [description, setDescription] = useState("");
  const [projectId, setProjectId] = useState("");
  const [taskId, setTaskId] = useState("");
  const [date, setDate] = useState(today);
  const [startTime, setStartTime] = useState("09:00");
  const [endTime, setEndTime] = useState("17:00");
  const [loading, setLoading] = useState(false);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [projectOpen, setProjectOpen] = useState(false);
  const [taskOpen, setTaskOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (entry) {
      setDescription(entry.description ?? "");
      setProjectId(entry.projectId ?? "");
      setTaskId(entry.taskId ?? "");
      const startDt = parseISO(entry.start);
      const endDt = parseISO(entry.end);
      setDate(format(startDt, "yyyy-MM-dd"));
      setStartTime(format(startDt, "HH:mm"));
      setEndTime(format(endDt, "HH:mm"));
      if (entry.projectId) {
        getProject(entry.projectId)
          .then((res) => setTasks((res?.tasks ?? []).filter((t) => !t.isArchived)))
          .catch(() => setTasks([]));
      }
    } else {
      setDescription("");
      setProjectId("");
      setTaskId("");
      setDate(defaultDate ?? today);
      setStartTime("09:00");
      setEndTime("17:00");
      setTasks([]);
    }
  }, [entry, open, today]);

  async function handleProjectSelect(id: string) {
    setProjectId(id);
    setTaskId("");
    setProjectOpen(false);
    if (id) {
      try {
        const res = await getProject(id);
        setTasks((res?.tasks ?? []).filter((t) => !t.isArchived));
      } catch {
        setTasks([]);
      }
    } else {
      setTasks([]);
    }
  }

  function handleTaskSelect(id: string) {
    setTaskId(id);
    setTaskOpen(false);
  }

  const canSubmit = !!projectId && !!taskId && !!description.trim();

  function checkOverlap(start: Date, end: Date): boolean {
    return existingEntries
      .filter((e) => !entry || e.id !== entry.id)
      .some((e) => {
        const eStart = parseISO(e.start);
        const eEnd = parseISO(e.end);
        return isBefore(start, eEnd) && isAfter(end, eStart);
      });
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;

    const start = parse(`${date} ${startTime}`, "yyyy-MM-dd HH:mm", new Date());
    const end = parse(`${date} ${endTime}`, "yyyy-MM-dd HH:mm", new Date());

    if (!isAfter(end, start)) {
      toast.error("End time must be after start time");
      return;
    }

    if (isAfter(start, addMinutes(new Date(), 1))) {
      toast.error("Start time cannot be in the future");
      return;
    }

    if (checkOverlap(start, end)) {
      toast.warning("This entry overlaps with an existing entry");
    }

    setLoading(true);

    if (isEdit) {
      await onSubmit({
        description: description || undefined,
        projectId: projectId || null,
        taskId: taskId || null,
        start: start.toISOString(),
        end: end.toISOString(),
      });
    } else {
      await onSubmit({
        description: description || undefined,
        projectId,
        taskId,
        start: start.toISOString(),
        end: end.toISOString(),
        createdWith: "web",
      });
    }

    setLoading(false);
    onOpenChange(false);
  }

  const activeProjects = projects.filter((p) => !p.isArchived);
  const selectedProject = activeProjects.find((p) => p.id === projectId);
  const selectedTask = tasks.find((t) => t.id === taskId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Entry" : "Add Entry"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Date + time + duration */}
          <div className="flex items-center gap-2">
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} max={today} className="flex-1" />
            <Input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} className="w-[100px]" />
            <span className="text-xs text-muted-foreground">—</span>
            <Input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} className="w-[100px]" />
            {startTime && endTime && (() => {
              const s = parse(`${date} ${startTime}`, "yyyy-MM-dd HH:mm", new Date());
              const e = parse(`${date} ${endTime}`, "yyyy-MM-dd HH:mm", new Date());
              const diff = (e.getTime() - s.getTime()) / 1000;
              if (diff > 0) {
                return <span className="ml-2 min-w-[60px] text-right text-sm font-semibold">{formatDuration(diff)}</span>;
              }
              return null;
            })()}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Project</Label>
              <Popover open={projectOpen} onOpenChange={setProjectOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    className="w-full justify-between text-sm font-normal"
                  >
                    {selectedProject ? (
                      <span className="flex items-center gap-2 truncate">
                        <span
                          className="inline-block h-2 w-2 rounded-full shrink-0"
                          style={{ backgroundColor: selectedProject.color ?? "#94A3B8" }}
                        />
                        <span className="truncate">{selectedProject.name}</span>
                      </span>
                    ) : (
                      <span className="text-muted-foreground">Select project...</span>
                    )}
                    <ChevronsUpDown className="ml-1 h-3 w-3 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[220px] p-0">
                  <Command>
                    <CommandInput placeholder="Search project..." />
                    <CommandList>
                      <CommandEmpty>No project found.</CommandEmpty>
                      <CommandGroup>
                        {activeProjects.map((p) => (
                          <CommandItem
                            key={p.id}
                            value={p.name}
                            onSelect={() => handleProjectSelect(p.id)}
                            className="flex items-center justify-between"
                          >
                            <span className="flex items-center gap-2">
                              <span
                                className="inline-block h-2 w-2 rounded-full shrink-0"
                                style={{ backgroundColor: p.color ?? "#94A3B8" }}
                              />
                              {p.name}
                            </span>
                            {projectId === p.id && <Check className="h-4 w-4 shrink-0" />}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <Label>Task</Label>
              <Popover open={taskOpen} onOpenChange={setTaskOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    className="w-full justify-between text-sm font-normal"
                    disabled={!projectId}
                  >
                    {selectedTask ? (
                      <span className="truncate">{selectedTask.name}</span>
                    ) : (
                      <span className="text-muted-foreground">Select task...</span>
                    )}
                    <ChevronsUpDown className="ml-1 h-3 w-3 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[220px] p-0">
                  <Command>
                    <CommandInput placeholder="Search task..." />
                    <CommandList>
                      <CommandEmpty>No task found.</CommandEmpty>
                      <CommandGroup>
                        {tasks.map((t) => (
                          <CommandItem
                            key={t.id}
                            value={t.name}
                            onSelect={() => handleTaskSelect(t.id)}
                            className="flex items-center justify-between"
                          >
                            <span>{t.name}</span>
                            {taskId === t.id && <Check className="h-4 w-4 shrink-0" />}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="entry-description">Note</Label>
            <Input
              id="entry-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What is this entry for?"
              required
            />
          </div>

          <DialogFooter>
            <Button variant="outline" type="button" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading || !canSubmit}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isEdit ? "Save" : "Add"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
